class CartProducts {
  static instances = new WeakMap();

  static pendingRefreshes = new WeakSet();

  static observedRoots = new WeakMap();

  static selector = '[data-cart-products]';

  static boot() {
    this.initializeAll();

    document.addEventListener('cart:updated', this.handleCartUpdate);
    document.addEventListener('cart:update', this.handleCartUpdate);
  }

  static initializeAll(root = document) {
    if (root instanceof Element && root.matches(this.selector)) {
      this.initialize(root);
    }

    root.querySelectorAll?.(this.selector).forEach((container) => this.initialize(container));
  }

  static initialize(container) {
    this.observeContainer(container);

    const currentInstance = this.instances.get(container);

    if (currentInstance) {
      currentInstance.refresh();
      return currentInstance;
    }

    const instance = new CartProducts(container);
    this.instances.set(container, instance);
    return instance;
  }

  static queueRefresh(container) {
    if (!container || this.pendingRefreshes.has(container)) return;

    this.pendingRefreshes.add(container);
    queueMicrotask(() => {
      this.pendingRefreshes.delete(container);
      if (container.isConnected) this.initialize(container);
    });
  }

  static handleCartUpdate() {
    queueMicrotask(() => CartProducts.initializeAll());
  }

  static observeContainer(container) {
    const observerRoot = container.closest('cart-items-component') || container;
    if (this.observedRoots.has(observerRoot)) return;

    const observer = new MutationObserver((mutations) => this.handleMutations(observerRoot, mutations));
    observer.observe(observerRoot, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    this.observedRoots.set(observerRoot, observer);
  }

  static handleMutations(observerRoot, mutations) {
    if (!mutations.some((mutation) => this.isRelevantMutation(mutation))) return;

    if (observerRoot.matches(this.selector)) {
      this.queueRefresh(observerRoot);
    }

    observerRoot.querySelectorAll(this.selector).forEach((container) => {
      this.queueRefresh(container);
    });
  }

  static isRelevantMutation(mutation) {
    const targetElement = mutation.target instanceof Element
      ? mutation.target
      : mutation.target.parentElement;

    if (mutation.type === 'characterData') {
      return Boolean(targetElement?.closest('[data-cart-products-config], [data-cart-variant-data]'));
    }

    if (targetElement?.matches('.original-price')) return false;

    const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];
    const onlyDiscountIndicators = changedNodes.length > 0 && changedNodes.every(
      (node) => node instanceof Element && node.classList.contains('discount-indicator')
    );
    if (onlyDiscountIndicators) return false;

    if (targetElement?.closest(this.selector)) return true;

    return changedNodes.some(
      (node) => node instanceof Element && (node.matches(this.selector) || node.querySelector(this.selector))
    );
  }

  constructor(container) {
    this.container = container;
    this.handleClick = this.handleClick.bind(this);
    this.init();
  }

  init() {
    this.container.addEventListener('click', this.handleClick);
    this.refresh();
  }

  refresh() {
    this.applyDiscountToOtherInstallments();
  }

  readJson(script) {
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Erro ao ler dados do carrinho:', error);
      return null;
    }
  }

  getConfig() {
    return this.readJson(this.container.querySelector('[data-cart-products-config]'));
  }

  getVariantData(itemKey) {
    const scripts = this.container.querySelectorAll('[data-cart-variant-data]');

    for (const script of scripts) {
      const data = this.readJson(script);
      if (data && String(data.itemKey) === String(itemKey)) return data;
    }

    return null;
  }

  getRowElement(itemKey) {
    return Array.from(this.container.querySelectorAll('[data-key]')).find(
      (element) => String(element.dataset.key) === String(itemKey)
    );
  }

  applyDiscountToOtherInstallments() {
    const discountBadge = this.container.querySelector('.celso-discount-badge');
    if (!discountBadge) return;

    const couponCodeElement = discountBadge.querySelector('.celso-discount-badge-code');
    if (!couponCodeElement) return;

    const couponCode = couponCodeElement.textContent.trim();
    console.log('💰 Aplicando desconto demais mensalidades para cupom:', couponCode);

    const config = this.getConfig();
    if (!config || !Array.isArray(config.products)) return;

    config.products.forEach((product) => this.applyProductDiscount(product, couponCode, config.installmentCount));
  }

  applyProductDiscount(product, couponCode, installmentCount) {
    const rowElement = this.getRowElement(product.itemKey);
    if (!rowElement || !product.cupomJson.cupons) return;

    const activeCoupon = product.cupomJson.cupons.find(
      (coupon) => coupon.cupom === couponCode || coupon.cupom.includes(couponCode.replace(/^PADRÃO-/, ''))
    );

    if (!activeCoupon || !activeCoupon.porcentagem_demais || activeCoupon.porcentagem_demais <= 0) return;

    const discountPercentage = parseFloat(activeCoupon.porcentagem_demais);
    const basePriceCents = product.precoCompareAt > 0 ? product.precoCompareAt : product.precoVariante;
    const discountedPrice = basePriceCents * (1 - discountPercentage / 100);
    const originalPriceElement = rowElement.querySelector('.original-price');
    if (!originalPriceElement) return;

    const installmentContainer = rowElement.querySelector('.cart-items__price-current--installment');
    const installments = Number(installmentCount) || 12;
    let displayedPriceCents = discountedPrice;

    if (installmentContainer && installments > 0) {
      displayedPriceCents = discountedPrice / installments;
    }

    const formattedPrice = installmentContainer
      ? (displayedPriceCents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : (displayedPriceCents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    console.log('📊 Produto:', product.productId, {
      cupomAtivo: activeCoupon.cupom,
      porcentagemDemais: discountPercentage + '%',
      precoRiscado: (product.precoCompareAt / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) + ' (compare_at_price)',
      precoAtualVerde: (product.precoOriginal / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) + ' (com cupom)',
      novoPrecoVerde: formattedPrice + (installmentContainer ? ' (parcela com porcentagem_demais)' : ' (com porcentagem_demais)'),
      calculo: installmentContainer
        ? `${(basePriceCents / 100).toFixed(2)} × (1 - ${discountPercentage}/100) ÷ ${installments} = ${(displayedPriceCents / 100).toFixed(2)}`
        : `${(basePriceCents / 100).toFixed(2)} × (1 - ${discountPercentage}/100) = ${(discountedPrice / 100).toFixed(2)}`,
    });

    if (originalPriceElement.textContent !== formattedPrice) {
      originalPriceElement.textContent = formattedPrice;
    }
    originalPriceElement.classList.add('original-price--discounted');
    originalPriceElement.title = `Preço com desconto de ${discountPercentage}% nas demais mensalidades`;

    if (!originalPriceElement.nextElementSibling?.classList.contains('discount-indicator')) {
      const discountIndicator = document.createElement('small');
      discountIndicator.className = 'discount-indicator';
      discountIndicator.textContent = `${discountPercentage}% off nas demais mensalidades`;
      originalPriceElement.parentNode.insertBefore(discountIndicator, originalPriceElement.nextSibling);
    }
  }

  handleClick(event) {
    const button = event.target.closest('.cart-modalidade-btn');
    if (!button || button.classList.contains('active') || button.disabled) return;

    const switcher = button.closest('.cart-modalidade-switcher');
    if (!switcher || !this.container.contains(switcher)) return;

    const itemKey = switcher.dataset.itemKey;
    const data = this.getVariantData(itemKey);
    if (!data) return;

    const newModality = button.dataset.modalidade;
    const newVariant = this.findVariant(data, newModality);
    if (!newVariant) return;

    this.updateModality(switcher, itemKey, data.properties || {}, newModality, newVariant.id);
  }

  findVariant(data, newModality) {
    const modalityIndex = Number(data.modIdx);
    const currentOptions = data.currentOptions;
    const variants = data.variants;
    let newVariant = null;

    for (let index = 0; index < variants.length; index += 1) {
      const variant = variants[index];
      if (String(variant.options[modalityIndex]) !== String(newModality)) continue;

      let matches = true;
      for (let optionIndex = 0; optionIndex < currentOptions.length; optionIndex += 1) {
        if (optionIndex === modalityIndex) continue;
        if (String(variant.options[optionIndex]) !== String(currentOptions[optionIndex].value)) {
          matches = false;
          break;
        }
      }

      if (matches) {
        newVariant = variant;
        break;
      }
    }

    if (!newVariant) {
      for (let index = 0; index < variants.length; index += 1) {
        if (String(variants[index].options[modalityIndex]) === String(newModality) && variants[index].available) {
          newVariant = variants[index];
          break;
        }
      }
    }

    if (!newVariant) {
      for (let index = 0; index < variants.length; index += 1) {
        if (String(variants[index].options[modalityIndex]) === String(newModality)) {
          newVariant = variants[index];
          break;
        }
      }
    }

    return newVariant;
  }

  updateModality(switcher, itemKey, properties, newModality, variantId) {
    this.setSwitcherDisabled(switcher, true);

    const newProperties = Object.assign({}, properties);
    newProperties.Modalidade = newModality;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: 0 }),
    })
      .then((response) => response.json())
      .then(() => fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties: newProperties }),
      }))
      .then((response) => response.json())
      .then(() => window.location.reload())
      .catch((error) => {
        console.error('Erro ao alterar modalidade:', error);
        this.setSwitcherDisabled(switcher, false);
      });
  }

  setSwitcherDisabled(switcher, disabled) {
    switcher.querySelectorAll('.cart-modalidade-btn').forEach((button) => {
      button.disabled = disabled;
    });
  }
}

CartProducts.boot();
